import { useRead, ReadState, isCollection } from "@/components/LoadingStates";
import { useAuth } from "@/context/AuthContext";
import IncludeDemoToggle from "@/components/IncludeDemoToggle";
import useIncludeDemo from "@/hooks/useIncludeDemo";
import { useState } from "react";
import SectionTabs, { SECTION_TABS } from "@/components/SectionTabs";
import api, { formatError } from "@/lib/api";
import { toast } from "sonner";

import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Library, Search } from "lucide-react";
import { classNames } from "@/lib/format";
import HookCard from "@/components/HookCard";
import SwipeFile from "@/components/SwipeFile";
import ExportButton from "@/components/ExportButton";

const PLATFORMS = [
  { key: "all", label: "All" },
  { key: "instagram", label: "Instagram" },
  { key: "tiktok", label: "TikTok" },
  { key: "youtube", label: "Shorts" },
];
const SORTS = [
  { key: "score", label: "Ancien score (non vérifié)" },
  { key: "multiplier", label: "Lift" },
  { key: "views", label: "Views" },
  { key: "recent", label: "Recent" },
];

export default function Patterns() {
  const { canEdit, user } = useAuth();
  const [includeDemo, setIncludeDemo] = useIncludeDemo(user?.workspace_id);
  const [tab, setTab] = useState("library");
  const [niche, setNiche] = useState("all");
  const [pattern, setPattern] = useState("all");
  const [platform, setPlatform] = useState("all");
  const [sort, setSort] = useState("score");
  const [q, setQ] = useState("");
  const libraryRead = useRead(`/patterns?niche=${niche}&pattern=${pattern}&platform=${platform}&sort=${sort}&q=${encodeURIComponent(q)}`, (d) => d && Array.isArray(d.items) && d.items.every((i) => i && typeof i.video_id === "string" && typeof i.outlier_multiplier === "number") && typeof d.total === "number" && d.facets && ["niches", "patterns", "platforms"].every((k) => Array.isArray(d.facets[k])));
  const swipesRead = useRead("/patterns/swipes", isCollection);
  const data = libraryRead.data;
  const swipes = swipesRead.data || [];
  const load = () => libraryRead.reload().catch(() => {});
  const loadSwipes = () => swipesRead.reload().catch(() => {});
  const hasFilters = niche !== "all" || pattern !== "all" || platform !== "all" || !!q;
  const resetFilters = () => { setNiche("all"); setPattern("all"); setPlatform("all"); setQ(""); };

  const toggleSwipe = async (item) => {
    if (!canEdit || libraryRead.status !== "success") return;
    try {
      if (item.swipe_id) {
        await api.delete(`/patterns/swipes/${item.swipe_id}`);
        toast.success("Removed from swipe file");
      } else {
        await api.post("/patterns/swipes", { video_id: item.video_id, hook_text: item.hook_text, pattern: item.pattern, niche: item.niche, platform: item.platform });
        toast.success("Saved to swipe file");
      }
      load();
      loadSwipes();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const pill = (active) => classNames("px-3 py-1.5 text-xs rounded-md transition-colors font-medium", active ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground");

  return (
    <div className="space-y-6" data-testid="patterns-page">
      <SectionTabs tabs={SECTION_TABS.studio} activeKey="patterns" ariaLabel="Studio sections" data-testid="section-tabs-studio" />
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Library className="h-6 w-6 text-primary" />
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">Pattern Library</h1>
          </div>
          <p className="text-muted-foreground text-sm mt-1">Interprétations des textes disponibles et anciennes analyses non vérifiées. Les nouvelles analyses ne calculent pas de score ; les scores absents restent indisponibles.</p>
        </div>
        <div className="flex items-center gap-2">
          {tab === "library" ? (
            <ExportButton path={`/export/patterns.csv?niche=${niche}&pattern=${pattern}&platform=${platform}&sort=${sort}&q=${encodeURIComponent(q)}`} testid="export-patterns-button" />
          ) : (
            <ExportButton path="/export/swipes.csv" testid="export-swipes-button" />
          )}
        <div className="flex gap-1 rounded-lg bg-muted p-1">
          <button onClick={() => setTab("library")} className={pill(tab === "library")} data-testid="patterns-tab-library">Library {data ? `(${data.total})` : ""}</button>
          <button onClick={() => setTab("swipes")} className={pill(tab === "swipes")} data-testid="patterns-tab-swipes">My swipe file ({swipes.length})</button>
        </div>
        </div>
      </div>

      <IncludeDemoToggle checked={includeDemo} onChange={setIncludeDemo} unavailable />
      <p className="text-sm text-muted-foreground" data-testid="patterns-evidence-notice">La vidéo et l’audio n’ont pas été analysés. Les explications de performance sont des hypothèses, pas des causes prouvées.</p>
      {tab === "swipes" ? (
        <ReadState read={swipesRead} scope="patterns-swipes">
        <SwipeFile swipes={swipes} onChange={() => { loadSwipes(); load(); }} />
        {!swipes.length && <div data-testid="patterns-swipes-empty"><button className="text-sm text-primary" onClick={() => setTab("library")} data-testid="patterns-swipes-empty-cta">Consulter Library</button></div>}
        </ReadState>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={niche} onValueChange={setNiche}>
              <SelectTrigger className="w-44" data-testid="patterns-niche-select"><SelectValue placeholder="Niche" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All niches</SelectItem>
                {(data?.facets.niches || []).map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={pattern} onValueChange={setPattern}>
              <SelectTrigger className="w-52" data-testid="patterns-pattern-select"><SelectValue placeholder="Pattern" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All patterns</SelectItem>
                {(data?.facets.patterns || []).map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="flex gap-1 rounded-lg bg-muted p-1">
              {PLATFORMS.map((p) => <button key={p.key} onClick={() => setPlatform(p.key)} className={pill(platform === p.key)} data-testid={`patterns-platform-${p.key}`}>{p.label}</button>)}
            </div>
            <div className="relative flex-1 min-w-[180px] max-w-xs">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search hooks…" className="pl-8" data-testid="patterns-search-input" />
            </div>
            <div className="flex gap-1 rounded-lg bg-muted p-1 ml-auto">
              {SORTS.map((s) => <button key={s.key} onClick={() => setSort(s.key)} className={pill(sort === s.key)} data-testid={`patterns-sort-${s.key}`}>{s.label}</button>)}
            </div>
          </div>

          <ReadState read={libraryRead} scope="patterns" empty={!data?.items.length} filtered={hasFilters} title={hasFilters ? "Aucun hook analysé pour ces filtres" : "Aucun hook analysé disponible"} description="Consultez Signals pour trouver des vidéos et leurs analyses." primaryCta={hasFilters ? { label: "Effacer les filtres", onClick: resetFilters } : { label: "Consulter Signals", to: "/outliers" }} legacyEmptyId="patterns-empty">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="patterns-grid">
              {data?.items.map((item) => <HookCard key={item.video_id} item={item} onToggle={() => toggleSwipe(item)} />)}
            </div>
          </ReadState>
        </>
      )}
    </div>
  );
}
